/**
 * Deterministic ordering for workout sets.
 *
 * Sets are ordered by their authoritative `sortIndex`, with ties broken by `id`
 * using code-unit comparison — which matches SQLite's default BINARY collation.
 * This keeps the in-memory ordering (e.g. the progress overview's
 * `useWorkoutSetSequence`) byte-identical to the SQL ordering used by the
 * builder query (`findSetsByExercise`, `ORDER BY sortIndex, id`), so the builder
 * and the overview never disagree — and neither depends on `createdAt`, which
 * ties when sets are cloned from a previous session in a single transaction.
 */
export interface SetOrderable {
  id: string | null;
  sortIndex: number | null;
}

export class SetOrdering {
  /**
   * Comparator: ascending `sortIndex`, then ascending `id` (code-unit order,
   * matching SQLite BINARY). A null `sortIndex` coalesces to 0 and a null `id`
   * to the empty string, so the ordering is always total and deterministic.
   */
  static compare(a: SetOrderable, b: SetOrderable): number {
    const bySortIndex = (a.sortIndex ?? 0) - (b.sortIndex ?? 0);
    if (bySortIndex !== 0) {
      return bySortIndex;
    }
    const idA = a.id ?? '';
    const idB = b.id ?? '';
    if (idA < idB) return -1;
    if (idA > idB) return 1;
    return 0;
  }

  /** Returns a new array sorted by {@link SetOrdering.compare}; input untouched. */
  static sort<T extends SetOrderable>(sets: readonly T[]): T[] {
    return [...sets].sort(SetOrdering.compare);
  }
}
